import { useMemo } from 'react';

export interface DeviceInfo {
  ua: string;
  deviceMemory: number | null;
  hardwareConcurrency: number | null;
  screenWidth: number | null;
  screenHeight: number | null;
  dpr: number;
  isTVUA: boolean;
  looksLikeTV: boolean;
  isMobileUA: boolean;
  looksLikeMobile: boolean;
  tvHint: string;
  memHint: string;
  touchSupport: boolean;
  maxTouchPoints: number;
}

export function useDeviceInfo(): DeviceInfo {
  return useMemo(() => {
    const ua = navigator.userAgent || '—';
    const dm = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null;
    const hc = navigator.hardwareConcurrency ?? null;
    const w = screen?.width ?? null;
    const h = screen?.height ?? null;
    const dpr = window.devicePixelRatio || 1;

    const isTVUA = /TV|SmartTV|HbbTV|Tizen|Web0S|NETTV|GoogleTV|Android TV|CrKey|AFT|BRAVIA|Viera|VIDAA/i.test(ua);
    const looksLikeTV = isTVUA || (w !== null && h !== null && Math.max(w, h) >= 1280 && Math.min(w, h) >= 720);

    const isMobileUA = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini|Mobile|mobile/i.test(ua);
    const touchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const maxTouchPoints = navigator.maxTouchPoints || 0;
    const looksLikeMobile = isMobileUA || (touchSupport && w !== null && Math.min(w, h ?? w) < 768);

    const memHint = typeof dm === 'number'
      ? dm <= 2 ? 'very low' : dm <= 4 ? 'low' : 'mid/high'
      : 'unknown';

    const tvHint = isTVUA
      ? 'Smart TV / Set-top box'
      : looksLikeMobile
        ? 'Mobile device'
        : looksLikeTV
          ? 'Likely TV / large screen'
          : 'Desktop / unknown';

    return {
      ua, deviceMemory: dm, hardwareConcurrency: hc,
      screenWidth: w, screenHeight: h, dpr,
      isTVUA, looksLikeTV, isMobileUA, looksLikeMobile,
      tvHint, memHint, touchSupport, maxTouchPoints,
    };
  }, []);
}
